import { parseBytes32String } from "@ethersproject/strings";
import { Contract, ethers } from "ethers";
import { ERC20 } from "@silo/typechain";
import { ERC20_ABI, ERC20_BYTES32_ABI } from "./abis";
import {
  ChainIdLegacy,
  ChecksumAddress,
  isSupportedChainId,
} from "@silo/types";
import {
  getTokenName,
  getTokenSymbol,
  isWrappedNativeToken,
} from "./nativeToken";
import { FallbackProvider } from "@ethersproject/providers";

interface Identifiers {
  symbol: string;
  rawSymbol: string;
  name: string;
  decimals: number;
  isNative: boolean;
}

const fetchSymbol = async (
  erc20Contract: ERC20,
  erc20BytesContract: Contract
) => {
  try {
    return await erc20Contract.symbol();
  } catch (e) {
    const bytes32Symbol = await erc20BytesContract.symbol();
    return parseBytes32String(bytes32Symbol);
  }
};

const fetchName = async (
  erc20Contract: ERC20,
  erc20BytesContract: Contract
) => {
  try {
    return await erc20Contract.name();
  } catch (e) {
    const bytes32Name = await erc20BytesContract.name();
    return parseBytes32String(bytes32Name);
  }
};

export const fetchIdentifiers = async (
  assetAddress: ChecksumAddress,
  provider: FallbackProvider,
  chainId: ChainIdLegacy,
  keepWrapped = false
): Promise<Identifiers> => {
  const erc20Contract = <ERC20>(
    new ethers.Contract(assetAddress, ERC20_ABI, provider)
  );
  const erc20CBytesContract = new ethers.Contract(
    assetAddress,
    ERC20_BYTES32_ABI,
    provider
  );

  const [decimals, symbol, name] = await Promise.all([
    erc20Contract.decimals(),
    fetchSymbol(erc20Contract, erc20CBytesContract),
    fetchName(erc20Contract, erc20CBytesContract),
  ]);

  if (keepWrapped) {
    return { decimals, symbol, name, isNative: false, rawSymbol: symbol };
  }

  return {
    decimals,
    rawSymbol: symbol,
    symbol: getTokenSymbol(chainId, assetAddress, symbol),
    name: getTokenName(chainId, assetAddress, name),
    isNative: isSupportedChainId(chainId)
      ? isWrappedNativeToken(chainId)(assetAddress)
      : false,
  };
};
